import React, { useEffect, useMemo, useState } from "react";
import { Form, Card, Badge, Spinner } from "react-bootstrap";
import SplitPane from "react-split-pane";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { studyLevelFormManagementServices } from "../../../../APIServices/studyLevelFormManagementServices";
import FormDateCalender from "../../FormManagement/CommonFiles/FormDateCalender";
import FormTimeSelection from "../../FormManagement/CommonFiles/FormTimeSelection";

const renderOperatorLabel = (value) => {
  switch (value) {
    case "===":
      return "=";
    case "/":
      return "÷";
    case "||":
      return "OR";
    case "&&":
      return "AND";
    case "if":
      return "IF";
    case "then":
      return "THEN";
    case "else":
      return "ELSE";
    case "else if":
      return "ELSE IF";
    default:
      return value;
  }
};

const OPTION_FIELD_TYPES = ["radio", "checkbox", "dropdown", "scale"];

const getQueryInputFields = (query = []) => {
  const seen = new Set();
  const fields = [];
  query.forEach((item) => {
    if (item?.type !== "parameter") return;
    const key = item?.value;
    if (!key || seen.has(key)) return;
    seen.add(key);
    fields.push(item);
  });
  return fields;
};

const resolveFieldMeta = (parameter, formFields = []) => {
  const fieldMeta = formFields.find(
    (item) => String(item?.id) === String(parameter?.fieldId)
  );
  if (!fieldMeta) {
    return { type: "text", options: [], fieldMeta: null };
  }

  const fieldCode = String(parameter?.value || "").split(".").pop();

  if (fieldCode === fieldMeta?.fieldName) {
    return {
      type: fieldMeta?.type || "text",
      options: fieldMeta?.options || [],
      fieldMeta,
    };
  }
  if (fieldCode === fieldMeta?.fieldUnitName) {
    return {
      type: "dropdown",
      options: fieldMeta?.unitOptions || [],
      fieldMeta: {
        ...fieldMeta,
        type: "dropdown",
        options: fieldMeta?.unitOptions || [],
      },
    };
  }
  if (fieldCode === fieldMeta?.referenceRangeLowerLimitName) {
    return {
      type: "dropdown",
      options: fieldMeta?.referenceRangeLowerLimitOptions || [],
      fieldMeta,
    };
  }
  if (fieldCode === fieldMeta?.referenceRangeUpperLimitName) {
    return {
      type: "dropdown",
      options: fieldMeta?.referenceRangeUpperLimitOptions || [],
      fieldMeta,
    };
  }
  if (fieldCode === fieldMeta?.referenceRangeIndicatorName) {
    return {
      type: "dropdown",
      options: fieldMeta?.referenceRangeIndicatorOptions || [],
      fieldMeta,
    };
  }
  if (fieldCode === fieldMeta?.clinicalSignificanceName) {
    return {
      type: "dropdown",
      options: [{ option: "CS" }, { option: "NCS" }],
      fieldMeta,
    };
  }
  if (OPTION_FIELD_TYPES.includes(fieldMeta?.type)) {
    return {
      type: fieldMeta.type,
      options: fieldMeta?.options || [],
      fieldMeta,
    };
  }
  return { type: fieldMeta?.type || "text", options: [], fieldMeta };
};

const isEmptyFormValue = (value) => {
  if (value === null || value === undefined || value === "") return true;
  if (typeof value === "object") {
    if ("day" in value || "month" in value || "year" in value) {
      return !value.day && !value.month && !value.year;
    }
    if ("hour" in value || "minute" in value || "second" in value) {
      return !value.hour && !value.minute && !value.second;
    }
  }
  return false;
};

const toEvalString = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    if ("day" in value || "month" in value || "year" in value) {
      return `${value.day ?? ""}${value.month ? `-${value.month}` : ""}${
        value.year ? `-${value.year}` : ""
      }`;
    }
    if ("hour" in value || "minute" in value) {
      return `${value.hour ?? ""}${value.minute ? `-${value.minute}` : ""}${
        value.second ? `-${value.second}` : ""
      }${value.format ? ` ${value.format}` : ""}`.trim();
    }
  }
  return value;
};

const getInitialValueForType = (type) => {
  if (type === "date") return { day: "", month: "", year: "" };
  if (type === "time") return { hour: "", minute: "", second: "", format: "" };
  return "";
};

// Same convertValue used in studyAIDataEntriesController buildExpression
const convertValue = (value) => {
  if (value === "MISSING") return "''";
  if (typeof value === "string" && value.trim() === "") return "''";
  if (!isNaN(value) && value !== "") return Number(value);
  return `'${value}'`;
};

// Mirrors backend cleanInvalidConditions
const cleanInvalidConditions = (rule) => {
  const invalidIds = new Set();
  const valueFields = rule.filter(
    (obj) =>
      Object.prototype.hasOwnProperty.call(obj, "valueData") &&
      ["parameter", "dynamicValue"].includes(obj.type)
  );

  const hasAnyValue = valueFields.some(
    (obj) => obj.valueData !== null && obj.valueData !== ""
  );

  if (!(valueFields.length > 1 && hasAnyValue)) {
    rule.forEach((obj) => {
      if (
        Object.prototype.hasOwnProperty.call(obj, "valueData") &&
        (obj.valueData === null || obj.valueData === "")
      ) {
        invalidIds.add(obj.id);
      }
    });
  }

  for (let i = 0; i < rule.length; i++) {
    if (invalidIds.has(rule[i].id)) {
      if (i > 0) invalidIds.add(rule[i - 1].id);
      if (i < rule.length - 1) invalidIds.add(rule[i + 1].id);
      if (
        i > 1 &&
        rule[i - 2]?.type === "operator" &&
        ["&&", "||"].includes(rule[i - 2].value)
      ) {
        invalidIds.add(rule[i - 2].id);
      }
    }
  }

  let cleaned = rule.filter((obj) => !invalidIds.has(obj.id));
  const stack = [];
  const valid = [];

  for (let i = 0; i < cleaned.length; i++) {
    const obj = cleaned[i];
    if (obj.type === "operator" && obj.value === "(") {
      stack.push({ obj, index: valid.length });
      valid.push(obj);
    } else if (obj.type === "operator" && obj.value === ")") {
      if (stack.length > 0) {
        stack.pop();
        valid.push(obj);
      }
    } else {
      valid.push(obj);
    }
  }

  const unmatchedOpenIndexes = new Set(stack.map((item) => item.index));
  return valid.filter((_, i) => !unmatchedOpenIndexes.has(i));
};

const collapseFunctions = (tokens) => {
  const checkFunction = tokens?.some((item) => item.type === "function");
  if (!checkFunction) return tokens;

  const out = [];
  let i = 0;
  while (i < tokens.length) {
    const t = tokens[i];
    if (t.type === "function") {
      let depth = 0;
      const slice = [];
      while (i < tokens.length) {
        const cur = tokens[i];
        slice.push(cur);
        if (cur.type === "operator" && cur.value === "(") depth++;
        else if (cur.type === "operator" && cur.value === ")") {
          depth--;
          if (depth === 0) {
            i++;
            break;
          }
        }
        i++;
      }
      const expr = slice
        .map((tok) => {
          if (tok.type === "function") return tok.value;
          if (tok.type === "operator") return tok.value;
          if (
            tok.type === "customValue" ||
            tok.type === "dynamicValue" ||
            tok.type === "parameter"
          ) {
            return `"${tok.valueData}"`;
          }
          return "";
        })
        .join("");
      out.push({
        id: t.id,
        type: "functionValue",
        value: expr,
        valueData: expr,
      });
    } else {
      out.push(t);
      i++;
    }
  }
  return out;
};

const checkControlKeywords = (rule) => {
  const keywords = ["if", "then", "else if", "else"];
  const operators = rule
    .filter((r) => r.type === "operator")
    .map((r) => r.valueData);
  return keywords.some((key) => operators.includes(key));
};

// Mirrors backend buildExpression
const buildExpression = (rule) =>
  rule
    .map((r) => {
      if (["customValue", "parameter", "dynamicValue"].includes(r.type)) {
        return convertValue(r.valueData);
      }
      return r.valueData;
    })
    .join(" ");

// Mirrors backend buildEvalCode (if / then / else)
const buildEvalCode = (rule) => {
  let resultCode = "let result;";
  let i = 0;
  while (i < rule.length) {
    const current = rule[i];
    if (
      current.type === "operator" &&
      (current.valueData === "if" || current.valueData === "else if")
    ) {
      const isElseIf = current.valueData === "else if";
      const thenIndex = rule.findIndex(
        (r, idx) => idx > i && r.type === "operator" && r.valueData === "then"
      );
      const nextElseOrElseIfIndex = rule.findIndex(
        (r, idx) =>
          idx > thenIndex &&
          r.type === "operator" &&
          (r.valueData === "else if" || r.valueData === "else")
      );
      const conditionTokens = rule.slice(i + 1, thenIndex);
      const actionTokens =
        nextElseOrElseIfIndex !== -1
          ? rule.slice(thenIndex + 1, nextElseOrElseIfIndex)
          : rule.slice(thenIndex + 1);
      const condition = conditionTokens
        .map((r) =>
          ["customValue", "parameter", "dynamicValue"].includes(r.type)
            ? convertValue(r.valueData)
            : r.valueData
        )
        .join(" ");
      const thenAction = actionTokens
        .map((r) =>
          ["customValue", "parameter", "dynamicValue"].includes(r.type)
            ? convertValue(r.valueData)
            : r.valueData
        )
        .join(" ");
      resultCode += ` ${isElseIf ? "else if" : "if"} (${condition}) { result = ${thenAction}; }`;
      i = thenIndex + actionTokens.length + 1;
    } else if (current.type === "operator" && current.valueData === "else") {
      const elseActionTokens = rule.slice(i + 1);
      const elseAction = elseActionTokens
        .map((r) =>
          ["customValue", "parameter", "dynamicValue"].includes(r.type)
            ? convertValue(r.valueData)
            : r.valueData
        )
        .join(" ");
      resultCode += ` else { result = ${elseAction}; }`;
      break;
    } else {
      i++;
    }
  }
  return resultCode;
};

const prepareRuleWithValues = (query = [], formValues = {}) =>
  query.map((item) => {
    const next = { ...item, valueData: item.value };
    if (item.type === "parameter") {
      next.valueData = toEvalString(formValues[item.value] ?? "");
    } else if (["customValue", "dynamicValue"].includes(item.type)) {
      next.valueData = item.value;
    } else if (item.type === "operator" || item.type === "function") {
      next.valueData = item.value;
    }
    return next;
  });

const buildPreviewEquation = (rule = []) =>
  rule
    .map((item) => {
      if (["parameter", "customValue", "dynamicValue"].includes(item.type)) {
        if (item.valueData === "" || item.valueData === null || item.valueData === undefined) {
          return item.value;
        }
        return item.valueData;
      }
      if (item.type === "operator") return renderOperatorLabel(item.valueData ?? item.value);
      if (item.type === "function" || item.type === "functionValue") {
        return item.valueData ?? item.value;
      }
      return item.valueData ?? item.value ?? "";
    })
    .filter((part) => part !== "")
    .join(" ");

const buildNormalQuery = (query = []) =>
  query
    .map((item) => {
      if (item?.type === "operator") return renderOperatorLabel(item.value);
      return item?.value ?? "";
    })
    .filter((part) => part !== "")
    .join(" ");

const buildQueryValueDisplay = (query = [], formValues = {}) =>
  query
    .map((item) => {
      if (item?.type === "parameter") {
        const filled = toEvalString(formValues[item.value]);
        return filled !== "" ? filled : item.value;
      }
      if (item?.type === "operator") return renderOperatorLabel(item.value);
      return item?.value ?? "";
    })
    .filter((part) => part !== "")
    .join(" ");

const convertQueryToEvalExpression = (query = [], formValues = {}) => {
  let rule = prepareRuleWithValues(query, formValues);
  rule = cleanInvalidConditions(rule);
  rule = collapseFunctions(rule).map((data) => {
    if (data?.type === "functionValue") {
      // YEAR(...) handling is backend-specific; keep expression string for preview
      return data;
    }
    return data;
  });

  const previewEquation = buildPreviewEquation(rule);
  let expression = "";
  let isControlFlow = false;

  if (!rule.length) {
    return { expression: "", previewEquation: "", cleanedRule: rule, isControlFlow };
  }

  if (checkControlKeywords(rule)) {
    isControlFlow = true;
    expression = buildEvalCode(rule);
  } else {
    expression = buildExpression(rule);
  }

  return { expression, previewEquation, cleanedRule: rule, isControlFlow };
};

const evaluateExpression = (expression, isControlFlow) => {
  if (!expression) {
    return { ok: false, value: undefined, error: "Empty expression" };
  }
  try {
    if (isControlFlow) {
      // eslint-disable-next-line no-new-func
      const runner = new Function(`${expression}; return result;`);
      const value = runner();
      if (value === undefined) {
        return { ok: false, value: undefined, error: "No result produced" };
      }
      return { ok: true, value };
    }
    // eslint-disable-next-line no-eval
    const value = eval(expression);
    if (value === undefined) {
      return { ok: false, value: undefined, error: "No result produced" };
    }
    return { ok: true, value };
  } catch (error) {
    return {
      ok: false,
      value: undefined,
      error: error?.message || "Evaluation failed",
    };
  }
};

const RuleTesting = ({ checkData, onTestCaseSaved, category }) => {
  const { studyId } = useParams();
  const StudyMode = JSON.parse(localStorage.getItem("StudyMode") || '""');
  const resultMode =
    category === "derived" || checkData?.category === "derived"
      ? "value"
      : "boolean";
  const query = useMemo(() => checkData?.query || [], [checkData?.query]);
  const inputFields = useMemo(() => getQueryInputFields(query), [query]);
  const [formValues, setFormValues] = useState({});
  const [fieldMetaMap, setFieldMetaMap] = useState({});
  const [optionsLoader, setOptionsLoader] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [runLoader, setRunLoader] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setFormValues({});
    setTestResult(null);
    setErrorMessage("");
  }, [checkData?._id, checkData?.checkId, inputFields]);

  useEffect(() => {
    if (!Object.keys(fieldMetaMap).length) return;
    setFormValues((prev) => {
      const next = { ...prev };
      inputFields.forEach((field) => {
        const meta = fieldMetaMap[field.value];
        const type = meta?.type || "text";
        if (next[field.value] === undefined) {
          next[field.value] = getInitialValueForType(type);
        } else if (type === "date" && typeof next[field.value] !== "object") {
          next[field.value] = getInitialValueForType("date");
        } else if (type === "time" && typeof next[field.value] !== "object") {
          next[field.value] = getInitialValueForType("time");
        }
      });
      return next;
    });
  }, [fieldMetaMap, inputFields]);

  useEffect(() => {
    let cancelled = false;

    const loadFieldMeta = async () => {
      if (!studyId || !inputFields.length) {
        setFieldMetaMap({});
        return;
      }

      setOptionsLoader(true);
      const formCache = {};
      const nextMetaMap = {};

      for (const field of inputFields) {
        const formId = field?.formId;
        const version = field?.version || "1";
        if (!formId) {
          nextMetaMap[field.value] = { type: "text", options: [], fieldMeta: null };
          continue;
        }

        const cacheKey = `${formId}__${version}`;
        if (!formCache[cacheKey]) {
          const response =
            await studyLevelFormManagementServices.getStudyLevelFormFieldsByVersion(
              studyId,
              formId,
              version
            );
          formCache[cacheKey] = response?.data?.formFields || [];
        }

        nextMetaMap[field.value] = resolveFieldMeta(field, formCache[cacheKey]);
      }

      if (!cancelled) {
        setFieldMetaMap(nextMetaMap);
        setOptionsLoader(false);
      }
    };

    loadFieldMeta();
    return () => {
      cancelled = true;
    };
  }, [studyId, inputFields]);

  const normalQuery = useMemo(() => buildNormalQuery(query), [query]);
  const queryValueDisplay = useMemo(
    () => buildQueryValueDisplay(query, formValues),
    [query, formValues]
  );

  const filledInputData = useMemo(() => {
    const data = {};
    Object.keys(formValues).forEach((key) => {
      if (!isEmptyFormValue(formValues[key])) {
        data[key] = toEvalString(formValues[key]);
      }
    });
    return data;
  }, [formValues]);

  const handleValueChange = (fieldKey, value) => {
    setFormValues((prev) => ({ ...prev, [fieldKey]: value }));
    setTestResult(null);
    setErrorMessage("");
  };

  const handleDatePartChange = (fieldKey, part, value) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldKey]: {
        day: "",
        month: "",
        year: "",
        ...(prev[fieldKey] || {}),
        [part]: value,
      },
    }));
    setTestResult(null);
    setErrorMessage("");
  };

  const handleTimePartChange = (fieldKey, part, value) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldKey]: {
        hour: "",
        minute: "",
        second: "",
        format: "",
        ...(prev[fieldKey] || {}),
        [part]: value,
      },
    }));
    setTestResult(null);
    setErrorMessage("");
  };

  const renderParameterInput = (field) => {
    const meta = fieldMetaMap[field.value] || { type: "text", options: [], fieldMeta: null };
    const fieldType = meta.type || "text";
    const options = meta.options || [];
    const currentValue = formValues[field.value];

    if (fieldType === "date" && meta.fieldMeta) {
      return (
        <FormDateCalender
          field={{
            ...meta.fieldMeta,
            fieldWidthSize: 100,
            dateFormat: meta.fieldMeta?.dateFormat || "DD_MM_YYYY",
          }}
          handleDateChange={(_id, part, value) =>
            handleDatePartChange(field.value, part, value)
          }
          dateValue={currentValue || {}}
          StudyMode=""
          studyPermission={{}}
          printable={false}
          editField={[meta.fieldMeta.id]}
        />
      );
    }

    if (fieldType === "time" && meta.fieldMeta) {
      return (
        <FormTimeSelection
          field={{
            ...meta.fieldMeta,
            fieldWidthSize: 100,
            timeFormat: meta.fieldMeta?.timeFormat || "hhmm",
            clockTimeFormat: meta.fieldMeta?.clockTimeFormat || "24",
          }}
          handleTimeChange={(_id, part, value) =>
            handleTimePartChange(field.value, part, value)
          }
          timeValue={currentValue || {}}
          StudyMode=""
          studyPermission={{}}
          printable={false}
          editField={[meta.fieldMeta.id]}
        />
      );
    }

    if (OPTION_FIELD_TYPES.includes(fieldType) || options.length > 0) {
      return (
        <Form.Select
          value={typeof currentValue === "string" ? currentValue : ""}
          onChange={(e) => handleValueChange(field.value, e.target.value)}
        >
          <option value="">Select option (optional)</option>
          {options.map((item, index) => (
            <option
              key={`${field.value}_${item?.option || index}`}
              value={item?.option ?? ""}
            >
              {item?.option}
            </option>
          ))}
        </Form.Select>
      );
    }

    if (fieldType === "number") {
      return (
        <Form.Control
          type="number"
          step={meta.fieldMeta?.numberType === "float" ? "any" : "1"}
          placeholder={`Enter number for ${field.value} (optional)`}
          value={typeof currentValue === "string" || typeof currentValue === "number" ? currentValue : ""}
          onChange={(e) => handleValueChange(field.value, e.target.value)}
        />
      );
    }

    if (fieldType === "textarea") {
      return (
        <Form.Control
          as="textarea"
          rows={2}
          placeholder={`Enter value for ${field.value} (optional)`}
          value={typeof currentValue === "string" ? currentValue : ""}
          onChange={(e) => handleValueChange(field.value, e.target.value)}
        />
      );
    }

    return (
      <Form.Control
        type="text"
        placeholder={`Enter value for ${field.value} (optional)`}
        value={typeof currentValue === "string" ? currentValue : ""}
        onChange={(e) => handleValueChange(field.value, e.target.value)}
      />
    );
  };

  const handleRun = async () => {
    if (!query.length) {
      setErrorMessage("Custom Check formula not found. Please build formula first.");
      return;
    }

    const { expression, previewEquation: equationPreview, isControlFlow } =
      convertQueryToEvalExpression(query, formValues);

    if (!expression) {
      setErrorMessage("Enter at least one field value to build a testable equation.");
      setTestResult(null);
      return;
    }

    const evalResult = evaluateExpression(expression, isControlFlow);
    let resultPayload;

    if (resultMode === "value") {
      // Derived Variable: show computed value (green), or invalid (red) — no Pass/Fail
      const isValid = evalResult.ok;
      const output = isValid ? String(evalResult.value) : "invalid";
      resultPayload = {
        passed: isValid,
        output,
        status: isValid ? "VALID" : "INVALID",
        message: output,
        description: isValid
          ? "Derived equation result."
          : evalResult.error || "Derived equation evaluation failed.",
        query: buildNormalQuery(query),
        queryValue: equationPreview,
        expression,
        inputData: filledInputData,
        resultMode: "value",
      };
    } else {
      // Auto Query / Notification / Inclusion-Exclusion / Field Notification: true/false
      if (!evalResult.ok) {
        setErrorMessage(
          `Unable to evaluate expression: ${expression}${
            evalResult.error ? ` (${evalResult.error})` : ""
          }`
        );
        setTestResult(null);
        return;
      }
      const isTrue = Boolean(evalResult.value);
      resultPayload = {
        passed: isTrue,
        output: String(isTrue),
        status: isTrue ? "PASS" : "FAIL",
        message: isTrue
          ? "PASS — Expression returned true"
          : "FAIL — Expression returned false",
        description: isTrue
          ? "Converted equation evaluated to true."
          : "Converted equation evaluated to false.",
        query: buildNormalQuery(query),
        queryValue: equationPreview,
        expression,
        inputData: filledInputData,
        resultMode: "boolean",
      };
    }

    setTestResult(resultPayload);
    setErrorMessage("");
    setRunLoader(true);

    try {
      const savePayload = {
        studyId,
        checkId: checkData?.checkId,
        checkRefId: checkData?._id,
        label: checkData?.label || "",
        category: category || checkData?.category || "",
        query: resultPayload.query,
        queryValue: equationPreview,
        inputData: filledInputData,
        expression,
        output: resultPayload.output,
        status: resultPayload.status,
        mode: StudyMode || "DBP Mode",
      };
      const response =
        await studyLevelFormManagementServices.addStudyLevelCustomEditCheckTestCase(
          savePayload
        );
      if (response?.statusCode === 200 || response?.type === "ADDED") {
        onTestCaseSaved?.(response?.data);
      }
    } catch (error) {
      console.error("Failed to save rule test case history", error);
    } finally {
      setRunLoader(false);
    }
  };

  return (
    <SplitPane
      split="vertical"
      minSize={"25%"}
      style={{ height: "-webkit-fill-available" }}
    >
      <div>
        <div className="p-3">
          <div className="text-muted small mb-2">
            Fields are optional. Fill only the values you want to test.
          </div>
          {!inputFields.length ? (
            <div className="text-muted small">
              No formula parameters found. Add parameters in Custom Check to generate this form.
            </div>
          ) : optionsLoader ? (
            <div className="text-muted small hstack gap-2">
              <Spinner size="sm" /> Loading field options...
            </div>
          ) : (
            inputFields.map((field) => (
              <Form.Group className="mb-3" key={field.id || field.value}>
                <Form.Label className="mb-1 fw-semibold">
                  {field.value}
                  {fieldMetaMap[field.value]?.type ? (
                    <span className="text-muted fw-normal ms-1">
                      ({fieldMetaMap[field.value].type})
                    </span>
                  ) : null}
                </Form.Label>
                {renderParameterInput(field)}
              </Form.Group>
            ))
          )}
        </div>
      </div>

      <>
        <Form.Group className="border-bottom p-2 bg-white position-sticky top-0 z-1">
          <div className="hstack justify-content-between gap-2 align-items-start">
            <div className="text-start flex-grow-1">
              <div className="text-muted small fw-semibold mb-1">Preview Equation</div>
              <div className="mb-1" style={{ fontSize: 11 }}>
                <span className="text-muted fw-semibold">Query: </span>
                <code
                  style={{
                    fontFamily: "Consolas, Monaco, 'Courier New', monospace",
                    color: "#c7254e",
                    backgroundColor: "#f9f2f4",
                    padding: "1px 4px",
                    borderRadius: 3,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {normalQuery || "Build a Custom Check formula to test here."}
                </code>
              </div>
              <div style={{ fontSize: 11 }}>
                <span className="text-muted fw-semibold">Query Value: </span>
                <code
                  style={{
                    fontFamily: "Consolas, Monaco, 'Courier New', monospace",
                    color: "#c7254e",
                    backgroundColor: "#f9f2f4",
                    padding: "1px 4px",
                    borderRadius: 3,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {queryValueDisplay || "-"}
                </code>
              </div>
            </div>
            <button
              className="updateProfile"
              disabled={runLoader || !query.length}
              onClick={handleRun}
            >
              {runLoader ? (
                <span className="hstack gap-1">
                  <Spinner size="sm" /> Run
                </span>
              ) : (
                "Run"
              )}
            </button>
          </div>
        </Form.Group>
        <div className="p-3" style={{ backgroundColor: "#f8f9fa" }}>
          {errorMessage && (
            <Card className="app-card bg-danger-subtle mb-3">
              <Card.Body className="p-3 text-danger small">{errorMessage}</Card.Body>
            </Card>
          )}

          {testResult && (
            <Card
              className={`app-card ${
                testResult.passed ? "bg-success-subtle" : "bg-danger-subtle"
              }`}
            >
              <Card.Body className="p-4">
                {testResult.resultMode === "value" ? (
                  <div className="mb-3">
                    <div className="text-muted small fw-semibold mb-1">Result</div>
                    <div
                      className={`fw-bold ${
                        testResult.passed ? "text-success" : "text-danger"
                      }`}
                      style={{ fontSize: 22 }}
                    >
                      {testResult.output}
                    </div>
                    {!testResult.passed && (
                      <div className="text-muted small mt-1">
                        {testResult.description}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: 32,
                        height: 32,
                        backgroundColor: testResult.passed ? "#8fe0a2" : "#f5b5b5",
                      }}
                    >
                      {testResult.passed ? (
                        <FaCheckCircle className="text-success" size={18} />
                      ) : (
                        <FaTimesCircle className="text-danger" size={18} />
                      )}
                    </div>
                    <div>
                      <div
                        className={`fw-bold ${
                          testResult.passed ? "text-success" : "text-danger"
                        }`}
                        style={{ fontSize: "16px" }}
                      >
                        {testResult.message}
                      </div>
                      <div className="text-muted small">{testResult.description}</div>
                    </div>
                  </div>
                )}

                <div className="mb-2">
                  <div className="text-muted small fw-semibold mb-1">
                    Input data: {JSON.stringify(testResult.inputData)}
                  </div>
                </div>

                <div className="mb-2" style={{ fontSize: 11 }}>
                  <span className="text-muted fw-semibold">Query: </span>
                  <code
                    style={{
                      fontFamily: "Consolas, Monaco, 'Courier New', monospace",
                      color: "#c7254e",
                      backgroundColor: "#f9f2f4",
                      padding: "1px 4px",
                      borderRadius: 3,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {testResult.query}
                  </code>
                </div>

                <div className="mb-2" style={{ fontSize: 11 }}>
                  <span className="text-muted fw-semibold">Query Value: </span>
                  <code
                    style={{
                      fontFamily: "Consolas, Monaco, 'Courier New', monospace",
                      color: "#c7254e",
                      backgroundColor: "#f9f2f4",
                      padding: "1px 4px",
                      borderRadius: 3,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {testResult.queryValue}
                  </code>
                </div>

                {testResult.resultMode !== "value" && (
                  <div className="mb-2">
                    <div className="text-muted small fw-semibold mb-1">
                      Result :{" "}
                      <Badge bg={testResult.passed ? "success" : "danger"}>
                        {testResult.status} ({testResult.output})
                      </Badge>
                    </div>
                  </div>
                )}

                <div className="text-muted small">
                  This run has been saved in Test Cases history.
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
      </>
    </SplitPane>
  );
};

export default RuleTesting;
